import React, { FC } from "react";
import { Container } from "../../../../components";
import { Card, Icon, Button } from "@telia/teddy";
import classnames from "classnames";
import { useDispatch } from "react-redux";
import Pages from "../../../../../../constants/pages";
import { ICartItem } from "../../../../../../models";
import { trackWebshopClickEvent } from "../../../../google-analytics/googleAnalyticsEventHelper";
import { toggleCartExpanded } from "../../../state/actions/cartActions";
import TEXT from "../../../text-consts";
import { getListingPageForItem } from "../../../utils";
import AddMemberContinueButton from "../../atoms/AddMemberContinueButton/AddMemberContinueButton";
import BackLink from "../../atoms/BackLink/BackLink";
import B2BPersonalDetailsContinueButton from "../../organisms/B2BPersonalDetailsContinueButton/B2BPersonalDetailsContinueButton";
import PersonalDetailsContinueButton from "../../organisms/PersonalDetailsContinueButton/PersonalDetailsContinueButton";
import SummaryContinueButton from "../../organisms/SummaryContinueButton/SummaryContinueButton";

import "./ContinueSection.less";

interface ContinueButtonProps {
    path: string;
}

interface BackButtonProps {
    hasSubscription: boolean;
    initiatedBy?: string;
    isMBB: boolean;
    isMinSideSimdealOrder: boolean;
    isReswitchAndHasNoMemberStep: boolean;
    lastAddedCartItem?: ICartItem;
    path: string;
}

interface ContinueSectionProps {
    className?: string;
    emptyOrder?: () => void;
    hasCreditCheckFailed?: boolean;
    hasSubscription?: boolean;
    initiatedBy?: string;
    isMBB: boolean;
    isMinSideSimdealOrder?: boolean;
    isReadyToContinue: boolean;
    isReswitchAndHasNoMemberStep?: boolean;
    lastAddedCartItem?: ICartItem;
    path: string;
    resetBankId?: () => void;
}

const ContinueButton: FC<ContinueButtonProps> = ({ path }) => {
    switch (path) {
        case Pages.CHECKOUT.PERSONAL_DETAILS.URL:
            return <PersonalDetailsContinueButton />;
        case Pages.CHECKOUT.SUMMARY.URL:
        case Pages.CHECKOUT_B2B.SUMMARY.URL:
            return <SummaryContinueButton />;
        case Pages.CHECKOUT.ADD_MEMBER.URL:
            return <AddMemberContinueButton />;
        case Pages.CHECKOUT_B2B.PERSONAL_DETAILS.URL:
            return <B2BPersonalDetailsContinueButton />;
        default:
            return null;
    }
};

const BackButton: FC<BackButtonProps> = ({
    path = "",
    isReswitchAndHasNoMemberStep,
    isMBB,
    hasSubscription,
    isMinSideSimdealOrder,
    lastAddedCartItem,
    initiatedBy,
}) => {
    let href = undefined;

    // Dont show back button on receipt
    if (path.includes(Pages.CHECKOUT.RECEIPT.URL)) {
        return null;
    }

    if (initiatedBy === "app" && path.includes(Pages.CHECKOUT.PERSONAL_DETAILS.URL)) {
        return <div />;
    }

    switch (true) {
        case lastAddedCartItem?.id.includes("COM12PURP20"):
            href = "/mobilabonnement/arsrabatter/";
            break;
        case lastAddedCartItem?.id.includes("TRYGG_B2C"):
            href = "https://www.telia.no/trygghet/tjenester/telia-trygg/";
            break;
        case path === Pages.CHECKOUT.PERSONAL_DETAILS.URL:
            href = isMinSideSimdealOrder
                ? "/minside/hjem?simdeals=true"
                : lastAddedCartItem?.href || getListingPageForItem(lastAddedCartItem);
            break;
        case path === Pages.CHECKOUT.SUMMARY.URL:
            href =
                !hasSubscription || isMBB || isReswitchAndHasNoMemberStep
                    ? Pages.CHECKOUT.PERSONAL_DETAILS.URL
                    : Pages.CHECKOUT.ADD_MEMBER.URL;
            break;
        case path === Pages.CHECKOUT.ADD_MEMBER.URL:
            href = Pages.CHECKOUT.PERSONAL_DETAILS.URL;
            break;
    }

    return <BackLink href={href} text={TEXT.COMMON.BACK} />;
};

const ContinueSection: FC<ContinueSectionProps> = ({
    className = "",
    isReadyToContinue,
    isMBB,
    isReswitchAndHasNoMemberStep = false,
    isMinSideSimdealOrder = false,
    path,
    hasSubscription = false,
    lastAddedCartItem,
    hasCreditCheckFailed,
    initiatedBy,
    resetBankId,
    emptyOrder,
}) => {
    const dispatch = useDispatch();

    const handleOnToggleCart = () => {
        // This click counts as a "click outside of cart" which triggers the cart to close. Adding a timout so that this closing cart event happens before opening.
        setTimeout(() => {
            dispatch(toggleCartExpanded(true));
        }, 0);
        // Track to GA
        trackWebshopClickEvent({
            action: "click",
            text: "Se bestilling",
            type: "button",
            url: "/kasse/oppsummering",
            context: "button_checkout",
        });
    };

    return (
        <Container
            className={classnames("beta-checkout--space-between", {
                [className]: className,
            })}
            size="small"
            noMargin
            data-cy="continue-section"
        >
            {[Pages.CHECKOUT.SUMMARY.URL as string, Pages.CHECKOUT_B2B.SUMMARY.URL as string].includes(path) && (
                <Card className="beta-checkout__toggle-cart" shadow layout="navigation-horizontal-small">
                    <Card.Content className="beta-checkout__toggle-cart__content">
                        <Card.Illustration>
                            <Icon size="md" name="cart" aria-hidden="true" focusable="false" />
                        </Card.Illustration>
                        <Card.Heading className="beta-checkout__toggle-cart__heading">
                            {TEXT.SUMMARY.SHOW_ORDER}
                        </Card.Heading>
                    </Card.Content>
                    <Card.Button variant="text" aria-label="show order" iconOnly onClick={handleOnToggleCart}>
                        <Icon size="md" name="chevron-right" aria-hidden="true" focusable="false" />
                    </Card.Button>
                </Card>
            )}
            <div className="beta-checkout__buttons-container">
                {!hasCreditCheckFailed && (
                    <BackButton
                        path={path}
                        isMBB={isMBB}
                        initiatedBy={initiatedBy}
                        hasSubscription={hasSubscription}
                        isMinSideSimdealOrder={isMinSideSimdealOrder}
                        isReswitchAndHasNoMemberStep={isReswitchAndHasNoMemberStep}
                        lastAddedCartItem={lastAddedCartItem}
                    />
                )}
                {isReadyToContinue && !hasCreditCheckFailed && (
                    <div className="beta-checkout__continue-button-container" data-cy="continue-button">
                        <ContinueButton path={path} />
                    </div>
                )}
                {hasCreditCheckFailed && (
                    <>
                        <Button onClick={emptyOrder} variant="destructive">
                            {TEXT.PERSONAL_DETAILS.VERIFICATION.CANCEL_ORDER}
                        </Button>
                        <Button onClick={resetBankId} variant="primary">
                            {TEXT.PERSONAL_DETAILS.VERIFICATION.RESET_BANKID}
                        </Button>
                    </>
                )}
            </div>
        </Container>
    );
};

export default ContinueSection;
